import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BeakerIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import Hero from '../components/sections/Hero';
import Features from '../components/sections/Features';
import HowItWorks from '../components/sections/HowItWorks';
import Contact from '../components/sections/Contact';
import { Button } from '../components/ui/Button';

const HomePage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle scroll to section based on the path
    if (location.pathname === '/features') {
      const featuresSection = document.getElementById('features');
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (location.pathname === '/how-it-works') {
      const howItWorksSection = document.getElementById('how-it-works');
      if (howItWorksSection) {
        howItWorksSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.pathname]);

  return (
    <>
      <Hero />
      
      {/* Platform Highlight Section */}
      <section className="py-16 bg-background">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-3xl font-extrabold text-text sm:text-4xl"
            >
              Two Powerful Platforms, One Vision
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="mt-4 max-w-2xl text-xl text-text mx-auto"
            >
              Comprehensive laboratory solutions for molecular diagnostics and personalized medicine
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Molecular Platform Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-background-alt rounded-lg shadow-lg p-8 border-l-4 border-primary hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light">
                    <BeakerIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-primary">Molecular Platform</h3>
                  <p className="text-sm text-text opacity-75">Advanced Microbiology Analysis</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <p className="text-text">
                  Transform complex microbiology data into comprehensive, interactive reports with pathogen detection, antimicrobial resistance analysis, and evidence-based treatment recommendations.
                </p>
                
                <ul className="space-y-2 text-sm text-text">
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-primary rounded-full mr-3 flex-shrink-0"></span>
                    Clinical Microbiology Analysis
                  </li>
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-primary rounded-full mr-3 flex-shrink-0"></span>
                    Antimicrobial Resistance Detection
                  </li>
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-primary rounded-full mr-3 flex-shrink-0"></span>
                    Treatment Recommendations
                  </li>
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-primary rounded-full mr-3 flex-shrink-0"></span>
                    Interactive Reporting
                  </li>
                </ul>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  href="/report"
                  variant="primary"
                  size="sm"
                  className="flex-1"
                >
                  View Demo Report
                </Button>
                <Button
                  href="/reports"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  All Reports
                </Button>
              </div>
            </motion.div>

            {/* PGx Platform Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-background-alt rounded-lg shadow-lg p-8 border-l-4 border-secondary hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-md bg-secondary text-text-light">
                    <CpuChipIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-secondary">PGx Platform</h3>
                  <p className="text-sm text-text opacity-75">Pharmacogenomics Testing</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <p className="text-text">
                  Personalized medicine through genetic analysis of drug metabolism, efficacy, and adverse reactions to optimize therapeutic outcomes and reduce adverse drug events.
                </p>
                
                <ul className="space-y-2 text-sm text-text">
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-secondary rounded-full mr-3 flex-shrink-0"></span>
                    Genetic Drug Metabolism Analysis
                  </li>
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-secondary rounded-full mr-3 flex-shrink-0"></span>
                    Personalized Dosing Recommendations
                  </li>
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-secondary rounded-full mr-3 flex-shrink-0"></span>
                    Adverse Drug Reaction Prediction
                  </li>
                  <li className="flex items-center">
                    <span className="h-2 w-2 bg-secondary rounded-full mr-3 flex-shrink-0"></span>
                    Precision Medicine Insights
                  </li>
                </ul>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  href="https://pgx.reasonshield.com"
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  isExternal
                >
                  Explore PGx Platform
                </Button>
                <Button
                  href="/contact"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Learn More
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-lg text-text mb-6">
              Ready to transform your laboratory data into actionable insights?
            </p>
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Schedule a Demo
            </Button>
          </motion.div>
        </div>
      </section>

      <Features />
      <HowItWorks />
      <div className="px-4 sm:px-6 text-center sm:text-center lg:text-center mb-8 sm:mb-12 bg-section-light py-10 sm:py-16">
        <div className="max-w-container mx-auto">
          <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Contact Us</h2>
          <p className="mt-2 text-2xl sm:text-3xl text-primary leading-8 font-extrabold tracking-tight sm:text-4xl">
            Get Started with Lab Results Processing
          </p>
          <p className="mt-3 sm:mt-4 max-w-2xl text-lg sm:text-xl text-primary mx-auto">
            Ready to transform your laboratory data into actionable clinical insights? Contact us to schedule a demo.
          </p>
          <Contact />
        </div>
      </div>
    </>
  );
};

export default HomePage; 